import { workflowRunAskTurnAskId } from './workflowRunAskTurnAskId'

interface CommandChipTurn {
  role: string
  askId?: string | null
  commandDisplayName?: string
  commandAudienceName?: string
}

interface CommandChipRun {
  token: string
  templateDisplayName: string
}

/**
 * Stamps `commandDisplayName` on the persisted user turn that started this
 * run (`askId === workflow-run-ask:<token>`). Only the current run's ask
 * gets a chip; older ask turns stay plain. Applied after `messageToTurn`
 * on load and refresh so F5 keeps the chip the optimistic start already
 * painted in-session.
 *
 * `audienceName` is the name the PAGE resolved for this run (plan
 * 2026-09-14-twin-test-bound-audience, B4): the run readback carries the
 * audience ID, never its name, so the caller hands over what its own audience
 * list resolved and F5 keeps BOTH chips once that list lands. Omitted or empty
 * leaves the audience stamp off entirely — the turn then shows the command chip
 * alone rather than a blank second chip.
 */
export function withRunCommandChip<T extends CommandChipTurn>(
  turns: Array<T>,
  run: CommandChipRun | null | undefined,
  audienceName?: string | null,
): Array<T> {
  if (!run) {
    return turns
  }

  const askId = workflowRunAskTurnAskId(run.token)
  return turns.map((turn) => {
    if (turn.role !== 'user' || turn.askId !== askId) {
      return turn
    }
    return {
      ...turn,
      commandDisplayName: run.templateDisplayName,
      ...(audienceName ? { commandAudienceName: audienceName } : {}),
    }
  })
}
